import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, Mail, Lock, Loader2, Building2 } from 'lucide-react';

const PartnerLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await login(email, password);
      if (user.role !== 'b2b_partner') {
        setError(user.role === 'admin' || user.role === 'employee' 
          ? 'Please use the staff portal to login.' 
          : 'This login is for B2B partners only. Please use the candidate login.');
        setLoading(false);
        return;
      }
      localStorage.removeItem('redirect_after_login');
      navigate('/partner/dashboard', { replace: true });
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F0E6] flex">
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          
          {/* <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 bg-[#FF5A36] rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-xl">E</span>
            </div>
            <span className="font-bold text-xl text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              EasyMyVisa
            </span>
          </Link> */}

           <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">        
                            <img src="/images/logo.png" alt="EasyMyVisa Logo" className=""  style={{ width: '250px', height: '150px' }} />
             </Link>

          {/* Partner Badge */}
          <div className="inline-flex items-center gap-2 bg-[#FF5A36]/10 text-[#FF5A36] px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
            <Building2 className="w-3.5 h-3.5" /> Partner Login
          </div>

          <h1 className="text-3xl font-bold text-[#0A1E40] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Partner Sign In
          </h1>
          <p className="text-[#4A5568] mb-8">
            Access your B2B partner dashboard to manage client applications.
          </p>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm" data-testid="partner-login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="partner-login-form">
            <div>
              <Label htmlFor="partner-email" className="text-[#0A1E40] font-medium">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF] pointer-events-none" />
                <Input
                  id="partner-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="partner@company.com"
                  className="pl-12"
                  required
                  data-testid="partner-email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="partner-password" className="text-[#0A1E40] font-medium">Password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF] pointer-events-none" />
                <Input
                  id="partner-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="pl-12 pr-12"
                  required
                  data-testid="partner-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#4A5568] hover:text-[#0A1E40]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#FF5A36] hover:bg-[#E0492B] text-white rounded-full py-6 font-semibold"
              data-testid="partner-login-btn"
            >
              {loading ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Signing in...</>
              ) : (
                'Sign In as Partner'
              )}
            </Button>
          </form>

          <p className="mt-8 text-center text-[#4A5568]">
            Don't have a partner account?{' '}
            <Link to="/partner/register" className="text-[#FF5A36] font-semibold hover:underline" data-testid="partner-register-link">
              Register as Partner
            </Link>
          </p>

          <p className="mt-3 text-center text-sm text-[#9CA3AF]">
            Looking for candidate login?{' '}
            <Link to="/login" className="text-[#0A1E40] hover:underline">
              Sign in here
            </Link>
          </p>
        </div>
      </div>

      {/* Right Panel */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-[#0A1E40]" />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center text-white max-w-md">
            <Building2 className="w-16 h-16 mx-auto mb-6 text-[#FF5A36]" />
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              B2B Partner Portal
            </h2>
            <p className="text-white/70 mb-8">
              Manage client visa applications, track commissions, and grow your business with special partner discounts.
            </p>
            <div className="grid grid-cols-3 gap-4">
              {[
                { val: '500+', label: 'Partners' },
                { val: '15%', label: 'Discount' },
                { val: '10%', label: 'Commission' },
              ].map((s, i) => (
                <div key={i} className="bg-white/10 rounded-xl p-3">
                  <p className="text-xl font-bold text-white">{s.val}</p>
                  <p className="text-white/50 text-xs">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerLoginPage;
